import { z } from 'zod';
import { crmLeadCnpj } from './crmCompanyData';

// Aba **Pessoas** do lead — quem decide na empresa e por onde falar com essa
// pessoa, pela API Kipflow (issue #220).
//
// É a terceira irmã de `crm_research/*`, e cada uma responde a uma pergunta
// diferente:
//
// - **dossiê** (`crmCompanyDossier`): como a empresa se apresenta ao mercado.
// - **cadastro** (`crmCompanyData`): o que a Receita afirma sobre ela.
// - **contatos** (aqui): com QUEM falar, e em qual e-mail/telefone.
//
// Duas chamadas alimentam esta aba, e é essa dupla que molda o contrato:
// `GET /contacts/v1/emails` (pessoas de maior senioridade, com e-mail GERADO a
// partir do padrão da empresa) e `GET /contacts/v1/phones` (telefones da
// empresa). Ambas são pagas, e a ordem do owner é a mesma da aba Dados: **uma
// busca por empresa, para sempre**.

/**
 * Quantos perfis do LinkedIn a geração de e-mails percorre, do mais sênior para
 * baixo. Fixo em 10 por ordem do owner: o custo escala com esse número, e a
 * lista existe para achar o decisor, não para mapear a empresa inteira.
 */
export const CRM_COMPANY_CONTACTS_EMAIL_LIMIT = 10;

/** Telefones da empresa por consulta — o custo é por telefone retornado. */
export const CRM_COMPANY_CONTACTS_PHONE_LIMIT = 3;

/**
 * Provedores de e-mail pessoais. Um lead cadastrado com `dono@gmail.com` tem
 * domínio, mas o domínio não é da empresa: consultar por ele geraria "os
 * decisores do gmail.com" — resposta absurda, cobrada, e **gravada para
 * sempre**, porque a trava é por identificador consultado.
 *
 * A lista é curta de propósito: erra para o lado de não consultar. Provedor
 * pessoal que escapar daqui custa uma consulta inútil; domínio corporativo
 * incluído por engano custaria a feature inteira naquele lead.
 */
const PERSONAL_EMAIL_DOMAINS = new Set([
  'gmail.com',
  'googlemail.com',
  'hotmail.com',
  'hotmail.com.br',
  'outlook.com',
  'outlook.com.br',
  'live.com',
  'msn.com',
  'yahoo.com',
  'yahoo.com.br',
  'ymail.com',
  'icloud.com',
  'me.com',
  'aol.com',
  'proton.me',
  'protonmail.com',
  'uol.com.br',
  'bol.com.br',
  'terra.com.br',
  'ig.com.br',
  'globo.com',
  'globomail.com',
  'r7.com',
  'zipmail.com.br',
]);

/**
 * A empresa que foi consultada. `CNPJ` é o identificador forte (a Kipflow casa a
 * empresa exata); `DOMAIN` é a reserva para o lead que ainda não tem documento
 * fiscal — a API aceita os dois.
 */
export const CrmCompanyContactKeySchema = z.object({
  kind: z.enum(['CNPJ', 'DOMAIN']),
  value: z.string().min(1),
});
export type CrmCompanyContactKey = z.infer<typeof CrmCompanyContactKeySchema>;

/**
 * Uma pessoa da empresa com e-mail **gerado** — não coletado. O endpoint monta o
 * endereço a partir do padrão do domínio e do nome do perfil; `validation` é o
 * veredito do fornecedor sobre ele, guardado como TEXTO VERBATIM (mesma postura
 * das flags do cadastro): um rótulo cru que o vendedor lê vale mais que um
 * booleano nosso que inventa certeza.
 *
 * `email` é `nullable` porque a geração falha para parte dos perfis. A pessoa
 * continua valendo: nome, cargo e LinkedIn identificam o decisor mesmo sem o
 * endereço.
 */
export const CrmCompanyContactPersonSchema = z.object({
  /** ID público do perfil — monta `linkedin.com/in/{linkedinId}`. */
  linkedinId: z.string().nullable(),
  /** Vem MAIÚSCULO e sem acento da API; gravado como veio. A UI é que apresenta. */
  fullName: z.string().nullable(),
  email: z.string().nullable(),
  /** `ENTREGAVEL` e afins — texto do fornecedor, não enum nosso. */
  validation: z.string().nullable(),
  seniority: z.string().nullable(),
  area: z.string().nullable(),
});
export type CrmCompanyContactPerson = z.infer<typeof CrmCompanyContactPersonSchema>;

/**
 * Telefone da EMPRESA (não da pessoa) — é o que o endpoint entrega.
 *
 * Campos batizados pela resposta REAL da API (conferida em 2026-08-04, dois
 * CNPJs): `telefone_completo`, `fixo_movel`, `whatsapp`, `categoria`.
 */
export const CrmCompanyContactPhoneSchema = z.object({
  /** Como a API entrega (`"41 985198510"`): com DDD, sem DDI, com espaço. */
  phone: z.string(),
  /** `FIXO` / `MOVEL` — pedimos só móvel, mas guardamos o que a API responder. */
  tipo: z.string().nullable(),
  whatsapp: z.boolean().nullable(),
  /**
   * Nota de qualidade do número, por LETRA (`A`…`C` observados). Texto, e não
   * número: os parâmetros `score_min`/`score_max` sugerem que existe um score
   * numérico na base, mas a resposta entrega a letra — e converter uma na outra
   * seria inventar uma escala que o fornecedor não publicou.
   */
  categoria: z.string().nullable(),
});
export type CrmCompanyContactPhone = z.infer<typeof CrmCompanyContactPhoneSchema>;

/**
 * `NOT_FOUND` também é resultado GRAVADO: a consulta sem match é cobrada igual, e
 * a mesma empresa devolveria o mesmo nada. Guardar o "não achamos" é o que
 * impede a segunda cobrança.
 */
export const CrmCompanyContactsSideStatusSchema = z.enum(['OK', 'NOT_FOUND']);
export type CrmCompanyContactsSideStatus = z.infer<typeof CrmCompanyContactsSideStatusSchema>;

/**
 * O que todo lado carrega além da lista. Escrito por extenso nos dois schemas
 * (em vez de um helper genérico) porque um construtor de schema em Zod não tem
 * tipo de retorno que se escreva à mão sem virar ruído.
 */
const sideBase = {
  status: CrmCompanyContactsSideStatusSchema,
  fetchedAt: z.number().int().nonnegative(),
  fetchedBy: z.string(),
  /**
   * Custo cobrado deste lado, em BRL e como a API mandou. Vai para a TELA (no
   * cabeçalho do lado) e para o acumulado do dia do usuário — em cents, via
   * `crmLookupCostCents`.
   *
   * `null` é o caso comum do "não achamos": os dois endpoints cobram por item
   * retornado, e a resposta sem match vem sem `cost` nem `pricing`. Aí o lado
   * aparece sem valor — dizer "custo não informado" num bloco que saiu de graça
   * sugeriria uma cobrança que não houve.
   */
  cost: z.number().nullable(),
};

export const CrmCompanyContactsPeopleSideSchema = z.object({
  ...sideBase,
  items: z.array(CrmCompanyContactPersonSchema).default([]),
});
export const CrmCompanyContactsPhonesSideSchema = z.object({
  ...sideBase,
  items: z.array(CrmCompanyContactPhoneSchema).default([]),
});
export type CrmCompanyContactsPeopleSide = z.infer<typeof CrmCompanyContactsPeopleSideSchema>;
export type CrmCompanyContactsPhonesSide = z.infer<typeof CrmCompanyContactsPhonesSideSchema>;

/**
 * O documento da aba.
 *
 * **A trava é por LADO cobrado, não pelo par.** "Uma busca por empresa, para
 * sempre" precisa conviver com DUAS chamadas HTTP independentes:
 *
 * - lado presente (`OK` ou `NOT_FOUND`) → foi cobrado, fica congelado;
 * - lado `null` → a chamada falhou do NOSSO lado (chave, cota, rede, timeout) e
 *   nada foi gravado; consultar de novo é o certo.
 *
 * Travar o par inteiro faria uma queda de rede nos telefones re-pagar os dez
 * e-mails que já tinham dado certo — o pior desfecho possível para dado pago.
 */
export const CrmCompanyContactsSchema = z.object({
  /**
   * O que foi CONSULTADO, não o que o lead tem hoje. É a chave da idempotência:
   * o servidor só devolve o salvo enquanto o lead continuar apontando para este
   * documento.
   */
  key: CrmCompanyContactKeySchema,
  fetchedAt: z.number().int().nonnegative(),
  fetchedBy: z.string(),
  /** Quando o último lado foi resolvido — `fetchedAt` guarda o primeiro. */
  updatedAt: z.number().int().nonnegative(),
  people: CrmCompanyContactsPeopleSideSchema.nullable().default(null),
  phones: CrmCompanyContactsPhonesSideSchema.nullable().default(null),
});
export type CrmCompanyContacts = z.infer<typeof CrmCompanyContactsSchema>;

/**
 * Id do doc em `crm_leads/{leadId}/crm_research`: **um por empresa consultada**,
 * não um por lead — mesma razão do `crmCompanyDataDocId`. Trocar o CNPJ do lead
 * (ou ele ganhar um CNPJ depois de já ter sido consultado por domínio) faz a aba
 * ler outro documento, em vez de sobrescrever e esconder o que já foi pago.
 *
 * O `kind` entra no ID porque CNPJ e domínio vivem em espaços diferentes: sem
 * ele, nada impediria (hoje ou depois de uma normalização nova) dois
 * identificadores distintos colidirem no mesmo documento.
 */
export function crmCompanyContactsDocId(key: CrmCompanyContactKey): string {
  return `contacts_${key.kind === 'CNPJ' ? 'cnpj' : 'dom'}_${key.value}`;
}

/**
 * Domínio aproveitável a partir de uma URL ou de um host solto, ou `null`.
 *
 * Aceita `https://www.Driva.io/sobre?x=1`, `driva.io` e `WWW.DRIVA.IO.`; recusa
 * qualquer coisa sem ponto, com caractere fora de `[a-z0-9-]` ou com TLD que não
 * seja alfabético. O resultado vai para o ID de um documento do Firestore, então
 * barra e espaço não podem sobreviver a esta função.
 */
export function crmNormalizeCompanyDomain(raw: string | null | undefined): string | null {
  if (raw == null) return null;
  let host = raw.trim().toLowerCase();
  if (host === '') return null;
  host = host.replace(/^[a-z][a-z0-9+.-]*:\/\//, '');
  // Credenciais em URL (`user:pass@host`) são raras, mas ficariam no domínio.
  host = host.slice(host.lastIndexOf('@') + 1);
  host = host.split(/[/?#]/)[0] ?? '';
  host = host.split(':')[0] ?? '';
  host = host.replace(/\.+$/, '').replace(/^www\./, '');
  if (host.length === 0 || host.length > 253) return null;
  return /^[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z]{2,}$/.test(host) ? host : null;
}

/** Domínio do e-mail, ou `null` quando é provedor pessoal (ver a lista). */
function corporateEmailDomain(email: string | null | undefined): string | null {
  if (email == null) return null;
  const at = email.lastIndexOf('@');
  if (at < 0) return null;
  const domain = crmNormalizeCompanyDomain(email.slice(at + 1));
  if (domain == null) return null;
  return PERSONAL_EMAIL_DOMAINS.has(domain) ? null : domain;
}

/**
 * A empresa que a aba Pessoas consulta — ou `null` quando não há como
 * identificá-la.
 *
 * A MESMA função decide o botão na UI e a recusa no servidor, pelo motivo de
 * sempre: duas cópias divergindo dariam botão habilitado que o servidor rejeita
 * (a lição do `crmMissingResearchFields`, repetida no `crmLeadCnpj`).
 *
 * Precedência, do mais forte para o mais fraco:
 *
 * 1. **CNPJ do lead** — identificador exato, e a maioria dos leads tem.
 * 2. **Domínio do site da empresa**, vindo do dossiê da aba Pesquisar.
 * 3. **Domínio do e-mail do lead**, e só se não for provedor pessoal.
 *
 * O e-mail vem por último de propósito: `contato@` de um domínio de terceiro
 * (agência, franqueadora) identifica a empresa errada, e o erro fica gravado.
 */
export function crmCompanyContactKey(
  lead: {
    taxId?: string | null | undefined;
    countryCode?: string | null | undefined;
    email?: string | null | undefined;
  },
  dossierWebsite: string | null | undefined,
): CrmCompanyContactKey | null {
  const cnpj = crmLeadCnpj(lead);
  if (cnpj != null) return { kind: 'CNPJ', value: cnpj };

  const site = crmNormalizeCompanyDomain(dossierWebsite);
  if (site != null) return { kind: 'DOMAIN', value: site };

  const fromEmail = corporateEmailDomain(lead.email);
  if (fromEmail != null) return { kind: 'DOMAIN', value: fromEmail };

  return null;
}

/** Os dois lados são pedidos juntos; o servidor consulta só o que falta. */
export const CrmFetchCompanyContactsInputSchema = z.object({
  boardId: z.string().min(1),
  leadId: z.string().min(1),
});
export type CrmFetchCompanyContactsInput = z.infer<typeof CrmFetchCompanyContactsInputSchema>;

export type CrmFetchCompanyContactsResult = {
  data: CrmCompanyContacts;
  /** `true` quando os dois lados já estavam salvos e nada foi consultado (nem cobrado). */
  cached: boolean;
};
